import React, { useState, useRef } from 'react';
import { DropdownMenu } from './DropdownMenu';

interface DropdownMenuItem {
  text: string;
}

interface SelectionContainerProps {
  title: string;
  items: DropdownMenuItem[];
  selectedItemText?: string;
  onItemSelect?: (itemText: string) => void;
  placeholder?: string;
  className?: string;
}

const styles = {
  container: {
    position: 'relative' as const,
    width: '100%',
    maxWidth: '100%',
  },
  trigger: {
    display: 'flex',
    padding: '12px',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: '10px',
    alignSelf: 'stretch',
    borderRadius: '8px',
    border: '1px solid #E5E5E5',
    borderBottom: '1px solid #E5E5E5',
    background: '#FFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    height: '80px',
    minHeight: '80px',
  },
  triggerOpen: {
    borderRadius: '8px 8px 0 0',
    borderBottom: '1px solid #E5E5E5',
  },
  triggerContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    gap: '4px',
    flex: 1,
    alignSelf: 'stretch',
    justifyContent: 'center',
  },
  titleText: {
    color: '#737373',
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '16px',
  },
  selectedText: {
    color: '#0A0A0A',
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '24px',
  },
  placeholderText: {
    color: '#9CA3AF',
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '20px',
  },
  chevron: {
    width: '20px',
    height: '20px',
    transition: 'transform 0.2s ease',
    color: '#6B7280',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  dropdown: {
    position: 'relative' as const,
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 50,
    border: '1px solid #E5E5E5',
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    background: '#FFF',
  },
};

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    style={{
      ...styles.chevron,
      ...(isOpen ? styles.chevronOpen : {}),
    }}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

export const SelectionContainer: React.FC<SelectionContainerProps> = ({
  title,
  items,
  selectedItemText,
  onItemSelect,
  placeholder = "Select an option...",
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTriggerClick = () => {
    setIsOpen(!isOpen);
  };

  const handleItemSelect = (itemText: string) => {
    onItemSelect?.(itemText);
  };

  const displayText = selectedItemText || placeholder;
  const isSelected = !!selectedItemText;

  return (
    <div 
      ref={containerRef}
      style={styles.container}
      className={className}
    >
      {/* Trigger Card */}
      <div
        style={{
          ...styles.trigger,
          ...(isOpen ? styles.triggerOpen : {}),
        }}
        onClick={handleTriggerClick}
      >
        <div style={styles.triggerContent}>
          <div style={isSelected ? styles.selectedText : styles.placeholderText}>
            {displayText}
          </div>
        </div>
        <ChevronIcon isOpen={isOpen} />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div style={styles.dropdown}>
          <DropdownMenu
            title={title}
            items={items}
            selectedItemText={selectedItemText}
            onItemSelect={handleItemSelect}
          />
        </div>
      )}
    </div>
  );
};